import { useEffect, useMemo, useState } from 'react';
import { useTourData, type TourEvidenceSource } from '../data/tour-data';
import { TourEvidenceStrengthBadge, iconForEvidence } from '../components/TourEvidencePanel';

export function TourDocumentsPage() {
  const fixture = useTourData();
  const [selectedId, setSelectedId] = useState(fixture.evidence_sources[0]?.document_id ?? '');
  const selected = fixture.evidence_sources.find((source) => source.document_id === selectedId) ?? fixture.evidence_sources[0];

  return (
    <section className="r42-page r42-page--wide r42-tour-workspace-page">
      <header className="r42-page-header r42-tour-page-header">
        <div><span className="r42-eyebrow">Machine knowledge</span><h1>Documents</h1><p>Different sources contribute different parts of the machine story.</p></div>
        <span className="r42-tour-readonly-pill"><i className="bi bi-lock" aria-hidden="true" /> 3 fixed sources</span>
      </header>

      <div className="r42-tour-docs-layout" data-tour-target="evidence-workspace">
        <div className="r42-tour-doc-list" data-tour-target="evidence-list">
          {fixture.evidence_sources.map((source) => (
            <button key={source.document_id} type="button" onClick={() => setSelectedId(source.document_id)} className={`r42-tour-doc-row${selected?.document_id === source.document_id ? ' is-active' : ''}`}>
              <span className="r42-tour-doc-row__icon"><i className={`bi ${iconForEvidence(source.evidence_type)}`} aria-hidden="true" /></span>
              <span className="r42-tour-doc-row__copy"><strong>{source.title}</strong><span>{source.original_filename}</span><small>{source.evidence_type} · Ready for questions</small></span>
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          ))}
        </div>
        {selected ? <DocumentPreview source={selected} /> : null}
      </div>
    </section>
  );
}

function DocumentPreview({ source }: { source: TourEvidenceSource }) {
  return (
    <article className="r42-tour-doc-preview">
      <header>
        <div><span className="r42-eyebrow">{source.evidence_type} evidence</span><h2>{source.title}</h2><p>{source.role_in_story}</p></div>
        <a className="btn btn-sm btn-outline-secondary" href={source.asset_path} target="_blank" rel="noopener noreferrer">Open source <i className="bi bi-box-arrow-up-right" aria-hidden="true" /></a>
      </header>

      <TourSourcePreview source={source} />

      <section className="r42-tour-doc-summary">
        <span className="r42-eyebrow">{String(source.preview.headline ?? 'Source summary')}</span>
        <p>{String(source.preview.summary ?? '')}</p>
      </section>

      <section className="r42-tour-claim-list">
        <div className="r42-tour-section-title"><div><span className="r42-eyebrow">Extracted evidence</span><h3>What Route42 can support from this source</h3></div><span>{source.claims.length} claims</span></div>
        {source.claims.map((claim) => (
          <div key={claim.claim_id} className="r42-tour-claim-row">
            <i className="bi bi-check2-circle" aria-hidden="true" />
            <p>{claim.statement}</p>
            <TourEvidenceStrengthBadge strength={claim.evidence_strength} />
          </div>
        ))}
      </section>
    </article>
  );
}

const PDF_PREVIEW_PATH = '/product-tour/route42-core-v1/evidence/nhvf-p101-operating-manual-page-1.png';

function TourSourcePreview({ source }: { source: TourEvidenceSource }) {
  if (source.evidence_type === 'IMAGE') {
    return (
      <div className="r42-tour-asset-preview r42-tour-asset-preview--image">
        <img src={source.asset_path} alt="Canonical P-101 control-panel evidence" />
      </div>
    );
  }

  if (source.evidence_type === 'CSV') return <TourCsvPreview source={source} />;

  const pageCount = Number(source.preview.page_count ?? 1);
  return (
    <div className="r42-tour-asset-preview r42-tour-asset-preview--pdf" aria-label="PDF preview">
      <div className="r42-tour-pdf-preview__page">
        <img src={PDF_PREVIEW_PATH} alt="Page 1 of the canonical P-101 operating and maintenance guide" />
      </div>
      <div className="r42-tour-pdf-preview__meta">Page 1 of {pageCount} · Open source to view the complete PDF</div>
    </div>
  );
}

function TourCsvPreview({ source }: { source: TourEvidenceSource }) {
  const [csvText, setCsvText] = useState<string>('');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setCsvText('');
    setLoadError(false);
    void fetch(source.asset_path, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('CSV preview unavailable');
        return response.text();
      })
      .then((text) => setCsvText(text))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoadError(true);
      });
    return () => controller.abort();
  }, [source.asset_path]);

  const rows = useMemo(() => parseCsv(csvText), [csvText]);
  const headers = rows[0] ?? [];
  const indexes = ['Event_Date', 'Component', 'Parameter', 'Observed_Value', 'Unit', 'Condition']
    .map((name) => headers.indexOf(name));
  const displayHeaders = ['Date', 'Component', 'Parameter', 'Observed', 'Unit', 'Condition'];

  return (
    <div className="r42-tour-asset-preview r42-tour-asset-preview--csv" aria-label="CSV preview">
      {!csvText && !loadError ? <div className="r42-tour-source-preview-state">Loading maintenance history…</div> : null}
      {loadError ? <div className="r42-tour-source-preview-state">Preview unavailable. Open source to view the complete CSV.</div> : null}
      {csvText && rows.length > 1 ? (
        <div className="r42-tour-csv-preview__scroll">
          <table className="r42-tour-csv-preview__table">
            <thead><tr>{displayHeaders.map((header) => <th key={header}>{header}</th>)}</tr></thead>
            <tbody>
              {rows.slice(1).map((row, rowIndex) => (
                <tr key={`${row[0] ?? 'row'}-${rowIndex}`}>
                  {indexes.map((index, columnIndex) => <td key={displayHeaders[columnIndex]}>{index >= 0 ? row[index] : ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function parseCsv(text: string): string[][] {
  if (!text.trim()) return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      row.push(field);
      field = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((candidate) => candidate.some((value) => value.length > 0));
}

